import React, {useMemo} from "react";
import {useMobileViewport} from "../../../../hooks/useMobileViewport";
import {formatLinkedCellText, formatLinkedHistoryAuthor, formatLinkedHistoryDate} from "./historyListCell";

const HISTORY_COLS =
    "minmax(88px, 11%) minmax(56px, 9%) minmax(0, 1fr) 14px minmax(0, 1fr) minmax(88px, 10%)";

function findColumn(columns, key) {
    return columns.find((col) => col.key === key);
}

export function isHistoryLinkedLayout(columns = []) {
    const keys = new Set(columns.map((col) => col.key));
    return keys.has("rawValue") && keys.has("updateValue");
}

function HistoryValue({text, variant}) {
    const value = text?.trim() ? text : "—";
    return (
        <span className={`asset-linked-history-value is-${variant}`} title={value !== "—" ? value : undefined}>
            {value}
        </span>
    );
}

export default function DetailHistoryLinkedList({rows, columns}) {
    const isMobileViewport = useMobileViewport();
    const fieldCol = useMemo(() => findColumn(columns, "fieldNameKr") ?? columns[0], [columns]);
    const authorCol = useMemo(() => findColumn(columns, "createdBy.name"), [columns]);
    const dateCol = useMemo(() => findColumn(columns, "createdAt"), [columns]);
    const gridStyle = useMemo(() => ({gridTemplateColumns: HISTORY_COLS}), []);

    if (isMobileViewport) {
        return (
            <div className="asset-linked-history-wrap asset-linked-history-wrap--mobile">
                <ul className="asset-linked-history-list asset-linked-history-list--mobile" role="list">
                    {rows.map((row, rowIndex) => {
                        const fieldName = fieldCol ? formatLinkedCellText(row, fieldCol) : "";
                        const author = formatLinkedHistoryAuthor(row, authorCol);
                        const dateText = dateCol ? formatLinkedHistoryDate(row, dateCol) : "";
                        const before = formatLinkedCellText(row, {key: "rawValue"});
                        const after = formatLinkedCellText(row, {key: "updateValue"});

                        return (
                            <li
                                key={row.id ?? rowIndex}
                                className="asset-linked-history-card"
                                aria-label={`${fieldName || "필드"} 수정 이력`}
                            >
                                <div className="asset-linked-history-card__head">
                                    <span className="asset-linked-history-field">{fieldName || "—"}</span>
                                    <span className="asset-linked-history-date">{dateText || "—"}</span>
                                </div>
                                <span className="asset-linked-history-author">{author || "—"}</span>
                                <div className="asset-linked-history-card__diff">
                                    <div className="asset-linked-history-card__value">
                                        <span className="asset-linked-history-card__label">수정전</span>
                                        <HistoryValue text={before} variant="before" />
                                    </div>
                                    <div className="asset-linked-history-card__value">
                                        <span className="asset-linked-history-card__label">수정후</span>
                                        <HistoryValue text={after} variant="after" />
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>
        );
    }

    return (
        <div className="asset-linked-history-wrap">
            <div className="asset-linked-history-cols" style={gridStyle} aria-hidden>
                <span>필드</span>
                <span>수정자</span>
                <span>수정전</span>
                <span />
                <span>수정후</span>
                <span>수정일</span>
            </div>
            <ul className="asset-linked-history-list" role="list">
                {rows.map((row, rowIndex) => {
                    const fieldName = fieldCol ? formatLinkedCellText(row, fieldCol) : "";
                    const author = formatLinkedHistoryAuthor(row, authorCol);
                    const dateText = dateCol ? formatLinkedHistoryDate(row, dateCol) : "";
                    const before = formatLinkedCellText(row, {key: "rawValue"});
                    const after = formatLinkedCellText(row, {key: "updateValue"});

                    return (
                        <li
                            key={row.id ?? rowIndex}
                            className="asset-linked-history-item"
                            style={gridStyle}
                            aria-label={`${fieldName || "필드"} 수정 이력`}
                        >
                            <span className="asset-linked-history-field">{fieldName || "—"}</span>
                            <span className="asset-linked-history-author" title={author || undefined}>
                                {author || "—"}
                            </span>
                            <HistoryValue text={before} variant="before" />
                            <span className="asset-linked-history-arrow" aria-hidden>
                                →
                            </span>
                            <HistoryValue text={after} variant="after" />
                            <span className="asset-linked-history-date" title={dateText || undefined}>
                                {dateText || "—"}
                            </span>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
